// Photo AI — Phase 6. Zone-scoped photo log with Claude Vision diagnosis.
// Photos upload to a zone; the diagnosis lands inline via PhotoLogCard.
// This page adds a zone picker so the crew can pick which green/tee/
// fairway they're looking at.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Zone } from '@greeniq/shared';
import { ApiError, api } from '../api/client.js';
import { useMe } from '../context/MeContext.js';
import { PageHeader } from '../components/ui/page-header';
import { PhotoLogCard } from '../components/PhotoLogCard.js';

interface CourseResponse { zones: Zone[] }

export function PhotoAIPage() {
  const { courseSlug } = useParams();
  const { me } = useMe();
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const role = me?.courses.find((c) => c.courseSlug === courseSlug)?.role;

  useEffect(() => {
    if (!courseSlug) return;
    api
      .get<CourseResponse>(`/courses/${courseSlug}`)
      .then((r) => {
        // Sort by zone type (greens first, then tees, then fairways, then rest)
        // so the crew gets the most useful defaults at the top of the picker.
        const order: Record<string, number> = {
          green: 0, tee_complex: 1, fairway: 2, rough: 3, bunker: 4,
        };
        const sorted = [...r.zones].sort((a, b) =>
          (order[a.zoneType] ?? 99) - (order[b.zoneType] ?? 99) || a.name.localeCompare(b.name),
        );
        setZones(sorted);
        setSelectedZoneId(sorted[0]?.id ?? null);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [courseSlug]);

  if (!courseSlug) return null;
  return (
    <div className="max-w-6xl p-6 md:p-10">
      <PageHeader
        eyebrow="Phase 6"
        title="Photo AI"
        description="Upload a photo from the course and Claude Vision diagnoses turf stress, disease, weeds, and irrigation issues. Photos + diagnoses stick to the zone so you can track the same spot over time."
      />
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <label className="text-[10px] uppercase tracking-[0.15em] text-fg-muted font-semibold">
          Zone
        </label>
        <select
          value={selectedZoneId ?? ''}
          onChange={(e) => setSelectedZoneId(Number(e.currentTarget.value))}
          className="rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-fg"
        >
          {zones.map((z) => (
            <option key={z.id} value={z.id}>
              {z.name} · {z.zoneType.replace('_', ' ')}
            </option>
          ))}
        </select>
      </div>
      {error && <p className="mt-4 text-sm text-[color:var(--color-error)]">{error}</p>}
      <div className="mt-4">
        {selectedZoneId !== null && (
          <PhotoLogCard courseSlug={courseSlug} zoneId={selectedZoneId} role={role} />
        )}
        {zones.length === 0 && !error && (
          <p className="text-sm text-fg-muted">Loading zones…</p>
        )}
      </div>
    </div>
  );
}
